import React, { useState, useEffect } from 'react';

const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      
      setCount(Math.floor(end * percentage));
      
      if (percentage < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return <span>{count.toLocaleString()}{suffix}</span>;
};

const Hero = ({ id, scrollToSection }) => {
  return (
    <section id={id} className="hero section">
      <div className="hero-content">
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          minHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: '2rem 0',
          position: 'relative'
        }}>
          
          {/* Animated Background */}
          <div className="hero-background">
            {/* Center Elements */}
            <div className="animated-dot dot-1"></div>
            <div className="animated-dot dot-2"></div>
            <div className="animated-dot dot-3"></div>
            <div className="animated-dot dot-4"></div>
            <div className="animated-dot dot-5"></div>
            <div className="animated-dot dot-6"></div>
            <div className="animated-dot dot-7"></div>
            <div className="animated-line line-1"></div>
            <div className="animated-line line-2"></div>
            <div className="animated-line line-3"></div>
            <div className="animated-line line-4"></div>
            <div className="animated-circle circle-1"></div>
            <div className="animated-circle circle-2"></div>
            <div className="animated-circle circle-3"></div>
            <div className="animated-square square-1"></div>
            <div className="animated-square square-2"></div>
            
            {/* Top Corner Elements */}
            <div className="animated-dot corner-dot-left"></div>
            <div className="animated-dot corner-dot-right"></div>
            <div className="animated-line corner-line-left"></div>
            <div className="animated-line corner-line-right"></div>
            <div className="animated-circle corner-circle-left"></div>
            <div className="animated-circle corner-circle-right"></div>
            <div className="animated-square corner-square-left"></div>
            <div className="animated-square corner-square-right"></div>
            
            {/* Bottom Corner Elements */}
            <div className="animated-dot bottom-corner-dot-left"></div>
            <div className="animated-dot bottom-corner-dot-right"></div>
            <div className="animated-line bottom-corner-line-left"></div>
            <div className="animated-line bottom-corner-line-right"></div>
          </div>
          
          {/* Avatar at Top */}
          <div style={{
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            marginBottom: '2rem',
            boxShadow: '0 10px 30px rgba(0, 212, 255, 0.3)',
            overflow: 'hidden',
            background: 'var(--color-bg-card)',
            border: '3px solid rgba(255, 255, 255, 0.1)'
          }}>
            <img 
              src="/images/avatar.jpg" 
              alt="Ravil Shaikh" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                // objectPosition: 'top',
                objectPosition: '50% 10%',
                borderRadius: '50%'
              }}
            />
          </div>

          {/* Main Content */}
          <div style={{ maxWidth: '700px', marginBottom: '3rem' }}>
            <h1 style={{
              fontSize: 'clamp(3rem, 6vw, 4.5rem)',
              fontWeight: '800',
              color: 'var(--color-primary)',
              margin: '0 0 1.5rem 0',
              lineHeight: '1.1',
              letterSpacing: '-0.02em'
            }}>
              I am Ravil Shaikh
            </h1>

            <p className="hero-description-shimmer" style={{
              fontSize: '1.3rem',
              lineHeight: '1.6',
              margin: '0 0 2rem 0',
              fontWeight: '400'
            }}>
              I help businesses scale their technology infrastructure from thousands to millions of users. Specialized in real-time systems, cloud architecture, and performance optimization to drive your digital transformation.
            </p>
          </div>

          {/* CTA Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '2rem'
          }}>
            <button 
              className="cta-button primary"
              onClick={() => scrollToSection('contact')}
              style={{
                fontSize: '1.2rem',
                padding: '1rem 2.5rem',
                fontWeight: '600'
              }}
            >
              Contact
            </button>
          </div>

          {/* Company Logo Carousel */}
          <div className="logo-carousel-container" style={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            overflow: 'hidden',
            marginBottom: '3rem',
            padding: '1rem 0',
            position: 'relative'
          }}>
            <div className="logo-carousel" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2rem',
              padding: '0 1rem',
              justifyContent: 'center',
              flexWrap: 'nowrap',
              width: '100%'
            }}>
              <div className="logo-item" style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                flexShrink: 0
              }}>
                <img src="/images/logos/eloelo-logo.png" alt="Eloelo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <div className="logo-item" style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                flexShrink: 0
              }}>
                <img src="/images/logos/hood-logo.png" alt="Hood" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <div className="logo-item" style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                flexShrink: 0
              }}>
                <img src="/images/logos/primathon-logo.png" alt="Primathon" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <div className="logo-item" style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                flexShrink: 0
              }}>
                <img src="/images/logos/easyeat-logo.png" alt="EasyEat" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <div className="logo-item" style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                flexShrink: 0
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-accent)',
                  fontSize: '2rem'
                }}>
                  🎵
                </div>
              </div>
            </div>
          </div>

          {/* Expertise Cards Row */}
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            marginBottom: '2rem',
            justifyContent: 'center'
          }}>


            <div className="hero-card" style={{
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              borderRadius: '12px',
              padding: '0.8rem 1.2rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer'
            }}>
              <span className="hero-icon lightning" style={{ fontSize: '1.2rem' }}>⚡</span>
              <div>
                <div className="hero-text-shimmer lightning" style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                  Scalable Systems
                </div>
                <div style={{ color: 'var(--color-secondary)', fontSize: '0.8rem' }}>
                  4+ Years Experience
                </div>
              </div>
            </div>

            <div className="hero-card" style={{
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              borderRadius: '12px',
              padding: '1rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer'
            }}>
              <span className="hero-icon rocket" style={{ fontSize: '1.2rem' }}>🚀</span>
              <div>
                <div className="hero-text-shimmer rocket" style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                  Innovation
                </div>
                <div style={{ color: 'var(--color-secondary)', fontSize: '0.8rem' }}>
                  Cutting-edge Technologies
                </div>
              </div>
            </div>

            <div className="hero-card" style={{
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              borderRadius: '12px',
              padding: '1rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer'
            }}>
              <span className="hero-icon robot" style={{ fontSize: '1.2rem' }}>🤖</span>
              <div>
                <div className="hero-text-shimmer robot" style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                  AI & ML
                </div>
                <div style={{ color: 'var(--color-secondary)', fontSize: '0.8rem' }}>
                  Machine Learning & LLMs
                </div>
              </div>
            </div>
          </div>

          {/* Education Card */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '3rem'
          }}>
            <a 
              href="https://jmi.ac.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hero-card"
              style={{
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '12px',
                padding: '1rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer'
              }}
            >
              <span className="hero-icon graduation" style={{ fontSize: '1.2rem' }}>🎓</span>
              <div>
                <div className="hero-text-shimmer graduation" style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                  B.Tech Computer Engineering
                </div>
                <div style={{ color: 'var(--color-secondary)', fontSize: '0.8rem' }}>
                  Jamia Millia Islamia
                </div>
              </div>
            </a>
          </div>



        </div>
      </div>
    </section>
  );
};

export default Hero; 